import { RefObject, useEffect } from "react";

import { resolveListKeyboardCommand } from "./keyboard";

export type ListKeyboardControllerOptions = {
  searchInputRef?: RefObject<HTMLElement>;
  onEscape?: () => void;
  rowSelector?: string;
  primaryActionSelector?: string;
  enabled?: boolean;
};

export const useListKeyboardController = ({
  searchInputRef,
  onEscape,
  rowSelector,
  primaryActionSelector = "[data-list-primary-action='true']",
  enabled = true,
}: ListKeyboardControllerOptions) => {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const command = resolveListKeyboardCommand(event, {
        search: Boolean(searchInputRef),
        escape: Boolean(onEscape),
        rows: Boolean(rowSelector),
      });

      if (command === "focus-search" && searchInputRef?.current) {
        event.preventDefault();
        searchInputRef.current.focus();
        return;
      }

      if (command === "escape" && onEscape) {
        onEscape();
        return;
      }

      if (!rowSelector || !command?.startsWith("row-")) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const currentRow = target?.closest?.(rowSelector) as HTMLElement | null;
      if (!currentRow) {
        return;
      }

      if (command === "row-activate") {
        if (target !== currentRow) {
          return;
        }
        const primaryAction = currentRow.querySelector<HTMLElement>(primaryActionSelector);
        if (primaryAction) {
          event.preventDefault();
          primaryAction.click();
        }
        return;
      }

      const rows = Array.from(
        currentRow.ownerDocument.querySelectorAll<HTMLElement>(rowSelector),
      ).filter((row) => row.getAttribute("aria-disabled") !== "true");
      const currentIndex = rows.indexOf(currentRow);
      const nextIndex = command === "row-next"
        ? Math.min(currentIndex + 1, rows.length - 1)
        : Math.max(currentIndex - 1, 0);

      if (currentIndex >= 0 && nextIndex !== currentIndex && rows[nextIndex]) {
        event.preventDefault();
        rows[nextIndex].focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, onEscape, primaryActionSelector, rowSelector, searchInputRef]);
};
